import { useEffect, useMemo, useState } from "preact/hooks";
import { api } from "../api";
import { Notice, QyooImage, Spinner } from "../components";
import {
  draftFromManifest,
  manifestFromDraft,
  type EditorDraft
} from "../domain";
import type { Capability, ExperienceType, ResolutionEnvelope } from "../types";
import type { WalletState } from "../wallet";

const types: Array<{ value: ExperienceType; label: string; description: string }> = [
  { value: "web", label: "Web", description: "Open ordinary HTTPS content" },
  { value: "interactiveWeb", label: "Interactive", description: "A permissioned Qyoo web experience" },
  { value: "redirect", label: "Redirect", description: "Hand off to an external HTTPS destination" },
  { value: "nativeAction", label: "Native", description: "A safe action built into the Qyoo app" },
  { value: "ar", label: "Native AR v2", description: "Explicit qyoob component or verified owner-hosted USDZ" }
];
const capabilities: Array<{ value: Capability; label: string }> = [
  { value: "shake", label: "Shake" },
  { value: "motion", label: "Motion / tilt" },
  { value: "share", label: "Share sheet" },
  { value: "haptic", label: "Haptic" },
  { value: "openExternal", label: "External link" }
];

export function EditorView(props: { qyoo: number; wallet: WalletState; navigate(path: string): void }) {
  const [details, setDetails] = useState<ResolutionEnvelope | null>(null);
  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "error" | "success" | "info"; text: string } | null>(null);

  useEffect(() => {
    void api.qyoo(props.qyoo).then((result) => {
      setDetails(result);
      setDraft(draftFromManifest(result.experience));
    }).catch((error) => setMessage({ kind: "error", text: error instanceof Error ? error.message : "Could not load Qyoo" }));
  }, [props.qyoo]);

  const preview = useMemo(() => {
    if (!draft) return null;
    try { return manifestFromDraft(props.qyoo, (details?.experience?.revision ?? details?.resolution.latestRevision ?? 0) + 1, draft); }
    catch { return null; }
  }, [details?.experience?.revision, details?.resolution.latestRevision, draft, props.qyoo]);
  const account = props.wallet.account?.toLowerCase();
  const authorized = !!account && (
    details?.blockchain.owner?.toLowerCase() === account ||
    details?.blockchain.controller?.toLowerCase() === account
  );
  const unsupportedNetwork = details !== null && ![1,11155111,31337].includes(details.chain.id);

  function set<K extends keyof EditorDraft>(key: K, value: EditorDraft[K]) {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  }

  function toggleCapability(capability: Capability) {
    if (!draft) return;
    set("capabilities", draft.capabilities.includes(capability)
      ? draft.capabilities.filter((value) => value !== capability)
      : [...draft.capabilities, capability]);
  }

  async function useNativeStarter() {
    try {
      const response = await fetch("/fixtures/native/amber-pyramid.json");
      if (!response.ok) throw new Error("The native starter could not load. Retry or use the developer guide.");
      const candidate = await response.json();
      setDraft(draftFromManifest(candidate));
      setMessage({kind: "info", text: "Amber native starter selected. Review, then sign to publish a new revision for your Qyoo."});
    } catch (error) { setMessage({kind: "error", text: error instanceof Error ? error.message : "Starter unavailable"}); }
  }

  async function publish() {
    if (!draft || !details || !props.wallet.account) return;
    setBusy(true);
    setMessage(null);
    try {
      if (unsupportedNetwork) throw new Error("Publication is unavailable on this network");
      if (props.wallet.chainId !== details.chain.id) await props.wallet.switchChain(details.chain.id);
      const manifest = manifestFromDraft(props.qyoo, (details.experience?.revision ?? details.resolution.latestRevision ?? 0) + 1, draft);
      const challenge = await api.challenge(props.qyoo, props.wallet.account, manifest);
      const signature = await props.wallet.signMessage(challenge.message);
      const result = await api.publish(props.qyoo, challenge.message, signature, manifest);
      setMessage({ kind: "success", text: `Published revision ${result.revision} as ${result.role}. It is ready to scan.` });
      setDetails({ ...details, experience: manifest, resolution: { status: "resolved", source: "official" } });
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Publication failed" });
    } finally {
      setBusy(false);
    }
  }

  if (!draft || !details) return <div class="page-shell">{message ? <Notice kind={message.kind}>{message.text}</Notice> : <Spinner label="Opening experience editor…" />}</div>;

  return (
    <div class="page-shell editor-page">
      <button class="text-button back" onClick={() => props.navigate(`/qyoo/${props.qyoo}`)}>← Qyoo #{props.qyoo}</button>
      <header class="page-intro"><p class="eyebrow">Versioned experiences</p><h1>Shape the experience.</h1><p>Configure what the Qyoo app opens. Publishing is authorized by an Ethereum signature—no account password.</p></header>
      {details.resolution.error && <Notice kind="error">{details.resolution.error.message}</Notice>}
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {!authorized && <Notice kind="error">Connect the current owner or delegated controller wallet to publish. The server rechecks Registry authority after signature verification.</Notice>}
      {unsupportedNetwork && <Notice kind="error">Publication is unavailable on this network.</Notice>}
      <p><button class="button ghost" disabled={busy} onClick={() => void useNativeStarter()}>Use amber native starter</button> · <a href="/developers">Host your own content</a></p>
      <div class="editor-layout">
        <form class="panel editor-form" onSubmit={(event) => { event.preventDefault(); void publish(); }}>
          <fieldset><legend>Identity in History</legend>
            <label for="title">Title</label><input id="title" required maxLength={120} value={draft.title} onInput={(event) => set("title", event.currentTarget.value)} />
            <label for="subtitle">Subtitle</label><textarea id="subtitle" maxLength={240} rows={3} value={draft.subtitle} onInput={(event) => set("subtitle", event.currentTarget.value)} />
            <label for="icon">Icon</label><input id="icon" value={draft.icon} placeholder="sf:sparkles or https://…" onInput={(event) => set("icon", event.currentTarget.value)} />
            <div class="color-row"><label>Background<input type="color" value={draft.background} onInput={(event) => set("background", event.currentTarget.value)} /></label><label>Text<input type="color" value={draft.foreground} onInput={(event) => set("foreground", event.currentTarget.value)} /></label></div>
          </fieldset>
          <fieldset><legend>Experience type</legend>
            <div class="type-grid">
              {types.map((item) => <button key={item.value} type="button" aria-pressed={draft.type === item.value} onClick={() => set("type", item.value)}><strong>{item.label}</strong><span>{item.description}</span></button>)}
            </div>
            {draft.type !== "nativeAction" && draft.type !== "ar" && <><label for="destination">HTTPS destination</label><input id="destination" type="url" required value={draft.url} onInput={(event) => set("url", event.currentTarget.value)} /></>}
            {draft.type === "nativeAction" && <Notice kind="info">This release supports the safe native action “show raw payload.”</Notice>}
            {draft.type === "interactiveWeb" && <div class="capabilities"><p>Requested capabilities</p>{capabilities.map((capability) => <label key={capability.value}><input type="checkbox" checked={draft.capabilities.includes(capability.value)} onChange={() => toggleCapability(capability.value)} />{capability.label}</label>)}</div>}
            {draft.type === "ar" && <><label for="native-content">Native v2 configuration</label><textarea id="native-content" rows={22} value={draft.nativeJSON} onInput={(event) => set("nativeJSON", event.currentTarget.value)} /><p class="field-help">Choose proceduralComponent (qyoo.qyoob/1) or remoteModel with an HTTPS USDZ, SHA-256, media type and exact byte length. Requires native runtime 2.0.0. Arbitrary v1 scene URLs are unsupported. Remote assets require network access each time.</p><label for="artwork">Verified History artwork (optional JSON asset descriptor)</label><textarea id="artwork" rows={4} value={draft.artworkJSON} onInput={(event) => set("artworkJSON", event.currentTarget.value)} /><p class="field-help">PNG/JPEG, at most 2 MiB and 4096 pixels per side. Leave Icon empty when choosing verified artwork.</p></>}
          </fieldset>
          <button class="button primary wide" type="submit" disabled={!authorized || busy || unsupportedNetwork || !preview}>{busy ? <Spinner label="Sign and publish…" /> : "Preview and publish"}</button>
          <p class="legal-note">The SIWE challenge is one-use and short-lived. Frontend visibility is never the authorization boundary.</p>
        </form>
        <aside class="history-preview" aria-label="History title and theme preview" style={{ background: draft.background, color: draft.foreground }}>
          <span class="phone-label">History title and theme</span>
          <QyooImage qyoo={props.qyoo} />
          <p class="eyebrow">{draft.type}</p><h2>{draft.title || "Untitled Qyoo"}</h2><p>{draft.subtitle}</p>
          <span class="id-chip">#{props.qyoo}</span>
          <details><summary>Manifest preview</summary><pre>{preview ? JSON.stringify(preview, null, 2) : "Fix the fields to preview this manifest."}</pre></details>
        </aside>
      </div>
    </div>
  );
}
